import React, { useState, useEffect, useRef } from 'react';
import { Icon } from '@/components/common/Icon';
import { Button, Checkbox, IconButton } from '@/components/common';
import { cn } from '@/utils/cn';
import { ScriptJobState } from '../hooks/useScriptJobStatus';

type RepairPhase = 'blocks' | 'structure' | 'props' | 'soundCues';

const PHASE_LABELS: Record<RepairPhase, { label: string; icon: string }> = {
    structure: { label: 'Show Structure', icon: 'account_tree' },
    props: { label: 'Props', icon: 'chair' },
    soundCues: { label: 'Sound Cues', icon: 'music_note' },
    blocks: { label: 'Script Blocks', icon: 'view_list' },
};

interface ScriptRepairPopoverProps {
    jobState: ScriptJobState;
    onClose: () => void;
    anchorRef: React.RefObject<HTMLElement | null>;
}

/**
 * Dropdown popover for inspecting import failures and triggering
 * phase-selective repair actions. Anchored below the status chip.
 */
export const ScriptRepairPopover: React.FC<ScriptRepairPopoverProps> = ({
    jobState,
    onClose,
    anchorRef,
}) => {
    const { status, blockCount, failedPhases, error, isRepairing, repairResult, repairJob } = jobState;
    const popoverRef = useRef<HTMLDivElement>(null);
    const repairError = typeof repairResult?.error === 'string' ? repairResult.error : null;

    // Pre-select failed phases
    const [selectedPhases, setSelectedPhases] = useState<Set<RepairPhase>>(() => {
        const initial = new Set<RepairPhase>();
        for (const p of failedPhases) {
            if (p in PHASE_LABELS) initial.add(p as RepairPhase);
        }
        return initial;
    });

    // Close on outside click
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (
                popoverRef.current &&
                !popoverRef.current.contains(e.target as Node) &&
                anchorRef.current &&
                !anchorRef.current.contains(e.target as Node)
            ) {
                onClose();
            }
        };
        document.addEventListener('mousedown', handler, { passive: true });
        return () => document.removeEventListener('mousedown', handler);
    }, [onClose, anchorRef]);

    const togglePhase = (phase: RepairPhase) => {
        setSelectedPhases(prev => {
            const next = new Set(prev);
            if (next.has(phase)) next.delete(phase);
            else next.add(phase);
            return next;
        });
    };

    const handleRepair = () => {
        if (selectedPhases.size === 0) return;
        void repairJob(Array.from(selectedPhases));
    };

    const isFailed = status === 'failed';

    return (
        <div
            ref={popoverRef}
            role="dialog"
            aria-label={isFailed ? 'Import Failed' : 'Import Incomplete'}
            data-testid="script-repair-popover"
            className={cn(
                'absolute top-full left-0 mt-2 z-50',
                'w-72 rounded-xl shadow-xl border',
                'bg-white dark:bg-gray-800',
                'border-gray-200 dark:border-gray-700',
                'animate-in fade-in slide-in-from-top-1 duration-200'
            )}
        >
            {/* Header */}
            <div className="px-4 pt-3 pb-2 border-b border-gray-100 dark:border-gray-700">
                <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                        {isFailed ? 'Import Failed' : 'Import Incomplete'}
                    </h3>
                    <IconButton
                        icon="close"
                        label="Close repair popover"
                        onClick={onClose}
                        size="sm"
                        variant="ghost"
                        className="rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    />
                </div>
                {blockCount > 0 && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {blockCount.toLocaleString()} blocks extracted
                    </p>
                )}
            </div>

            {/* Error summary */}
            {error ? <div className="px-4 py-2 bg-red-50 dark:bg-red-900/20 border-b border-gray-100 dark:border-gray-700">
                    <p className="text-xs text-red-600 dark:text-red-400 line-clamp-3">
                        {error}
                    </p>
                </div> : null}

            {/* Phase selection */}
            {!repairResult && (
                <div className="px-4 py-3">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                        Select phases to re-run:
                    </p>
                    <div className="space-y-1.5">
                        {(Object.keys(PHASE_LABELS) as RepairPhase[]).map((phase) => {
                            const { label, icon } = PHASE_LABELS[phase];
                            const isFailed = failedPhases.includes(phase);
                            const isSelected = selectedPhases.has(phase);

                            return (
                                <div
                                    key={phase}
                                    className={cn(
                                        'flex items-center gap-2.5 px-2 py-1.5 rounded-lg cursor-pointer transition-colors',
                                        isSelected
                                            ? 'bg-primary/10 dark:bg-primary/20'
                                            : 'hover:bg-gray-50 dark:hover:bg-white/5'
                                    )}
                                >
                                    <Checkbox
                                        checked={isSelected}
                                        onChange={() => togglePhase(phase)}
                                        disabled={isRepairing}
                                    />
                                    <Icon name={icon} size="sm" className="text-gray-500 dark:text-gray-400" />
                                    <span className="text-sm text-gray-700 dark:text-gray-300 flex-1">{label}</span>
                                    {isFailed ? <span className="text-[10px] font-medium text-amber-600 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/30 px-1.5 py-0.5 rounded-full">
                                            Failed
                                        </span> : null}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Repair result */}
            {repairResult ? <div className="px-4 py-3">
                    {repairError ? (
                        <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
                            <Icon name="error" size="sm" />
                            <p className="text-sm">{repairError}</p>
                        </div>
                    ) : (
                        <div className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400">
                            <Icon name="check_circle" size="sm" />
                            <p className="text-sm">Repair completed successfully</p>
                        </div>
                    )}
                </div> : null}

            {/* Actions */}
            <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between">
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => { jobState.dismiss(); onClose(); }}
                    className="min-h-0! p-0! text-xs text-gray-400 hover:bg-transparent hover:text-gray-600 dark:hover:text-gray-300"
                >
                    Dismiss
                </Button>
                {!repairResult && (
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={handleRepair}
                        disabled={selectedPhases.size === 0 || isRepairing}
                        leftIcon={isRepairing ? 'progress_activity' : 'build'}
                    >
                        {isRepairing ? 'Repairing...' : 'Repair'}
                    </Button>
                )}
                {repairResult ? <Button
                        variant="ghost"
                        size="sm"
                        onClick={onClose}
                    >
                        Close
                    </Button> : null}
            </div>
        </div>
    );
};
